import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, PencilSimple } from "@phosphor-icons/react";
import TaskEditDialog from "@/components/TaskEditDialog";

/**
 * Engagement Tasks tab.
 *
 * Iter 85.63 · Every task row now opens the shared canonical
 * TaskEditDialog so an edit here writes the same PUT /api/tasks/{id} the
 * firm-wide board uses. No parallel state, no duplicate copies — the
 * server is the source of truth and every surface reflects the same
 * canonical Task.
 */
export default function EngagementTasksTab({
  engagement,
  tasks = [],
  onNewTask,
  engagements = [],
  activities = [],
  users = [],
  onTaskChanged,
}) {
  const [editingTask, setEditingTask] = useState(null);

  return (
    <div className="border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-5 py-3">
        <h3 className="font-display font-bold text-sm">Tasks</h3>
        <Button
          size="sm"
          onClick={onNewTask}
          className="rounded-none bg-primary text-primary-foreground"
          data-testid="engagement-new-task-btn"
        >
          <Plus size={12} className="mr-1" /> New task
        </Button>
      </div>
      <table className="w-full text-sm">
        <thead className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
          <tr className="border-b border-border">
            <th className="text-left px-5 py-2 font-normal">Title</th>
            <th className="text-left px-3 py-2 font-normal">Assignee</th>
            <th className="text-left px-3 py-2 font-normal">Priority</th>
            <th className="text-left px-3 py-2 font-normal">Due</th>
            <th className="text-left px-3 py-2 font-normal">Status</th>
            <th className="px-3 py-2 font-normal w-8"></th>
          </tr>
        </thead>
        <tbody>
          {tasks.length === 0 && <tr><td colSpan="6" className="px-5 py-8 text-center text-xs text-muted-foreground">No tasks yet.</td></tr>}
          {tasks.map((t) => (
            <tr
              key={t.id}
              className="group border-b border-border/50 hover:bg-secondary/40 cursor-pointer transition-colors"
              onClick={() => setEditingTask(t)}
              data-testid={`engagement-task-row-${t.id}`}
            >
              <td className="px-5 py-3 font-medium">{t.title}</td>
              <td className="px-3 py-3 text-xs">{
                (users.find((u) => u.id === t.assignee_id) || {}).name
                || <span className="text-muted-foreground">—</span>
              }</td>
              <td className="px-3 py-3 text-[0.625rem] font-mono uppercase tracking-widest">{t.priority || "—"}</td>
              <td className="px-3 py-3 text-xs">{t.due_date || <span className="text-muted-foreground">—</span>}</td>
              <td className="px-3 py-3 text-[0.625rem] font-mono uppercase tracking-widest">{t.status}</td>
              <td className="px-3 py-3 text-right">
                <PencilSimple
                  size={13}
                  weight="bold"
                  className="opacity-0 group-hover:opacity-100 text-muted-foreground group-hover:text-primary transition-opacity inline-block"
                  aria-label={`Edit ${t.title}`}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <TaskEditDialog
        open={!!editingTask}
        onOpenChange={(o) => { if (!o) setEditingTask(null); }}
        task={editingTask}
        engagements={engagements}
        activities={activities}
        users={users}
        onSaved={() => onTaskChanged?.()}
        onDeleted={() => onTaskChanged?.()}
      />
    </div>
  );
}
